/**
 * Secondary Spend export box — sits at the top of the Outlets by Time (exec)
 * screen in its own panel with its own From/To period selector (defaults to the
 * current NYMR season). The "SS XLS" button downloads a real .xlsx from
 * GET /api/dashboards/exec/secondary-spend-xlsx?from=&to= (fetch → blob →
 * <a download>, no popup). Every outlet on one sheet each: hourly Value / TXNS
 * by operating day with timetable colours, plus a Summary sheet.
 */
import { useMemo, useState, type CSSProperties } from "react";

function currentFiscalYear(): number {
  const n = new Date();
  return n.getUTCMonth() + 1 >= 3 ? n.getUTCFullYear() + 1 : n.getUTCFullYear();
}
function seasonDefault(): { from: string; to: string } {
  const fy = currentFiscalYear();
  const lastFeb = new Date(Date.UTC(fy, 2, 0)).getUTCDate();
  return { from: `${fy - 1}-03-01`, to: `${fy}-02-${String(lastFeb).padStart(2, "0")}` };
}

const box: CSSProperties = {
  border: "1px solid #d8e0db", borderRadius: 10, background: "#fff",
  padding: "16px 18px", margin: "0 0 18px", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};
const title: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 700, color: "#1F4D3F" };
const sub: CSSProperties = { margin: "4px 0 12px", fontSize: 13, color: "#5a5a5a", maxWidth: 720 };
const toolbar: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" };
const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#333" };
const input: CSSProperties = { padding: "6px 8px", border: "1px solid #cfd8d2", borderRadius: 6, fontSize: 14 };
const btn: CSSProperties = {
  padding: "8px 16px", border: "none", borderRadius: 6, background: "#1F4D3F", color: "#fff",
  fontWeight: 700, fontSize: 14, cursor: "pointer",
};

export function SecondarySpendBox() {
  const def = useMemo(seasonDefault, []);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    if (from > to) { setError("'From' must be on or before 'To'."); return; }
    setBusy(true); setError(null);
    try {
      const res = await fetch(`/api/dashboards/exec/secondary-spend-xlsx?from=${from}&to=${to}`, { credentials: "include" });
      if (!res.ok) {
        const b = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(b.error || `Request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `secondary-spend_${from}_to_${to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={box}>
      <h2 style={title}>Secondary Spend export</h2>
      <p style={sub}>
        Downloads the Secondary Spend workbook (SS XLS) for the chosen period — one sheet per outlet with
        hourly Value (net £) and transaction count on each operating day, tagged with the timetable colour, plus
        a Summary sheet. Choose a date range and download.
      </p>
      <div style={toolbar}>
        <label style={field}><span>From</span>
          <input style={input} type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={field}><span>To</span>
          <input style={input} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" style={{ ...btn, opacity: busy ? 0.6 : 1, cursor: busy ? "wait" : "pointer" }} onClick={download} disabled={busy}>
          {busy ? "Building…" : "⬇ SS XLS"}
        </button>
        {error && <span style={{ color: "#b83a2b", fontSize: 13 }}>{error}</span>}
      </div>
    </section>
  );
}
